import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ChevronDown, ChevronRight, FileSpreadsheet, FileText, HelpCircle } from 'lucide-react';
import { memo, useMemo, useState } from 'react';
// ─── Helpers ──────────────────────────────────────────────────────────────────
function padNumber(n) {
    const num = Number.parseInt(n, 10);
    if (Number.isNaN(num)) return n || 'zzzz';
    return num.toString().padStart(4, '0');
}
function toDocNode(file) {
    return {
        file,
        docType: file.classification?.documentType?.toUpperCase() || file.subtype?.toUpperCase() || '',
        primaryType: file.classification?.primaryType?.toUpperCase() || '',
        amendmentNumber: file.amendmentNumber || '',
        associatedAmendment: file.associatedAmendment || '',
        confidence: file.classification?.confidence || 0
    };
}
function formatType(dt) {
    const labels = {
        SOLICITATION: 'Solicitation',
        AMENDMENT: 'Amendment',
        COST_VOLUME: 'Cost Volume',
        STAFFING_ROSTER: 'Staffing',
        QUESTIONS_AND_ANSWERS: 'Q&A',
        ATTACHMENT: 'Attachment',
        RFP: 'RFP',
        RFQ: 'RFQ'
    };
    return labels[dt] || dt.replace(/_/g, ' ').replace(/\b\w/g, (c)=>c.toUpperCase());
}
function shortName(name) {
    // Remove the numeric timestamp suffix (e.g., ".1579891572273")
    return name.replace(/\.\d{10,}\s*(\(\d+\))?(?=\.\w+$)/, '');
}
const TYPE_STYLES = {
    SOLICITATION: {
        dot: 'bg-blue-500',
        badge: 'bg-blue-50 text-blue-700 ring-blue-600/20'
    },
    RFP: {
        dot: 'bg-blue-500',
        badge: 'bg-blue-50 text-blue-700 ring-blue-600/20'
    },
    RFQ: {
        dot: 'bg-blue-500',
        badge: 'bg-blue-50 text-blue-700 ring-blue-600/20'
    },
    AMENDMENT: {
        dot: 'bg-amber-500',
        badge: 'bg-amber-50 text-amber-700 ring-amber-600/20'
    },
    COST_VOLUME: {
        dot: 'bg-emerald-500',
        badge: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20'
    },
    STAFFING_ROSTER: {
        dot: 'bg-violet-500',
        badge: 'bg-violet-50 text-violet-700 ring-violet-600/20'
    },
    QUESTIONS_AND_ANSWERS: {
        dot: 'bg-cyan-500',
        badge: 'bg-cyan-50 text-cyan-700 ring-cyan-600/20'
    },
    ATTACHMENT: {
        dot: 'bg-gray-400',
        badge: 'bg-gray-50 text-gray-600 ring-gray-500/20'
    }
};
function getStyle(dt) {
    return TYPE_STYLES[dt] || TYPE_STYLES.ATTACHMENT;
}
// ─── Categorization ───────────────────────────────────────────────────────────
function categorizeFiles(files) {
    const nodes = files.map(toDocNode);
    let baseDoc;
    const amendmentGroups = new Map();
    const baseAttachments = [];
    const supportingDocs = [];
    const qaDocs = [];
    for (const node of nodes){
        const isSolicitation = node.primaryType === 'BASE' || node.primaryType === 'SOURCE' || [
            'SOLICITATION',
            'RFP',
            'RFQ'
        ].includes(node.docType);
        const isAmendment = node.primaryType === 'AMENDMENT' || node.docType === 'AMENDMENT' || node.docType === 'MODIFICATION';
        const isQA = node.primaryType === 'QA' || node.docType === 'QUESTIONS_AND_ANSWERS';
        const isSolAmendment = isAmendment && (node.docType === 'AMENDMENT' || node.docType === 'MODIFICATION');
        const isAttachmentAmendment = isAmendment && !isSolAmendment && node.amendmentNumber !== '';
        if (isSolicitation && !baseDoc) {
            baseDoc = node;
        } else if (isSolAmendment && node.amendmentNumber) {
            const n = node.amendmentNumber;
            if (!amendmentGroups.has(n)) amendmentGroups.set(n, {
                number: n,
                sortKey: padNumber(n),
                attachments: []
            });
            const g = amendmentGroups.get(n);
            if (g) g.solicitationDoc = node;
        } else if (isAttachmentAmendment || node.associatedAmendment) {
            const n = node.associatedAmendment || node.amendmentNumber;
            if (n) {
                if (!amendmentGroups.has(n)) amendmentGroups.set(n, {
                    number: n,
                    sortKey: padNumber(n),
                    attachments: []
                });
                amendmentGroups.get(n)?.attachments.push(node);
            } else {
                supportingDocs.push(node);
            }
        } else if (isQA) {
            qaDocs.push(node);
        } else if ([
            'COST_VOLUME',
            'STAFFING_ROSTER'
        ].includes(node.docType)) {
            if (node.amendmentNumber) {
                const n = node.amendmentNumber;
                if (!amendmentGroups.has(n)) amendmentGroups.set(n, {
                    number: n,
                    sortKey: padNumber(n),
                    attachments: []
                });
                amendmentGroups.get(n)?.attachments.push(node);
            } else {
                baseAttachments.push(node);
            }
        } else if (node.docType === 'ATTACHMENT' || node.primaryType === 'ATTACHMENT') {
            if (node.amendmentNumber) {
                const n = node.amendmentNumber;
                if (!amendmentGroups.has(n)) amendmentGroups.set(n, {
                    number: n,
                    sortKey: padNumber(n),
                    attachments: []
                });
                amendmentGroups.get(n)?.attachments.push(node);
            } else {
                supportingDocs.push(node);
            }
        } else {
            supportingDocs.push(node);
        }
    }
    const sortedGroups = Array.from(amendmentGroups.values()).sort((a, b)=>-a.sortKey.localeCompare(b.sortKey));
    return {
        baseDoc,
        baseAttachments,
        sortedGroups,
        supportingDocs,
        qaDocs
    };
}
// ─── Components ───────────────────────────────────────────────────────────────
function DocItem({ node }) {
    const style = getStyle(node.docType);
    const isSpreadsheet = node.file.name.match(/\.(xlsx|csv|xls)$/i);
    const Icon = isSpreadsheet ? FileSpreadsheet : node.primaryType === 'QA' ? HelpCircle : FileText;
    return /*#__PURE__*/ _jsxs("div", {
        className: "group flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-gray-50 transition-colors",
        children: [
            /*#__PURE__*/ _jsx(Icon, {
                className: "h-4 w-4 text-gray-400 flex-shrink-0 group-hover:text-gray-500"
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "flex-1 min-w-0",
                children: /*#__PURE__*/ _jsx("p", {
                    className: "text-sm text-gray-800 truncate",
                    title: node.file.name,
                    children: shortName(node.file.name)
                })
            }),
            /*#__PURE__*/ _jsx("span", {
                className: `inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${style.badge}`,
                children: formatType(node.docType)
            }),
            node.confidence > 0 && /*#__PURE__*/ _jsxs("span", {
                className: "text-[10px] tabular-nums text-gray-400 w-8 text-right",
                children: [
                    Math.round(node.confidence * 100),
                    "%"
                ]
            })
        ]
    });
}
function TimelineNode({ group, isLatest, isLast, defaultOpen }) {
    const [open, setOpen] = useState(defaultOpen);
    const count = (group.solicitationDoc ? 1 : 0) + group.attachments.length;
    return /*#__PURE__*/ _jsxs("div", {
        className: "relative flex gap-4",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-col items-center flex-shrink-0 w-6",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: `w-3 h-3 rounded-full ring-4 ring-white z-10 ${isLatest ? 'bg-blue-500' : 'bg-gray-300'}`
                    }),
                    !isLast && /*#__PURE__*/ _jsx("div", {
                        className: "w-px flex-1 bg-gray-200"
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex-1 pb-6 min-w-0",
                children: [
                    /*#__PURE__*/ _jsxs("button", {
                        className: "flex items-center gap-2 w-full text-left group",
                        onClick: ()=>setOpen(!open),
                        children: [
                            open ? /*#__PURE__*/ _jsx(ChevronDown, {
                                className: "h-3.5 w-3.5 text-gray-400"
                            }) : /*#__PURE__*/ _jsx(ChevronRight, {
                                className: "h-3.5 w-3.5 text-gray-400"
                            }),
                            /*#__PURE__*/ _jsxs("span", {
                                className: `text-sm font-semibold ${isLatest ? 'text-gray-900' : 'text-gray-600'}`,
                                children: [
                                    "Amendment ",
                                    group.number
                                ]
                            }),
                            isLatest && /*#__PURE__*/ _jsx("span", {
                                className: "inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold text-blue-700",
                                children: "CURRENT"
                            }),
                            /*#__PURE__*/ _jsxs("span", {
                                className: "text-xs text-gray-400 ml-auto",
                                children: [
                                    count,
                                    " ",
                                    count === 1 ? 'file' : 'files'
                                ]
                            })
                        ]
                    }),
                    open && /*#__PURE__*/ _jsxs("div", {
                        className: "mt-1 ml-5 space-y-0",
                        children: [
                            group.solicitationDoc && /*#__PURE__*/ _jsx(DocItem, {
                                node: group.solicitationDoc
                            }),
                            group.attachments.map((att)=>/*#__PURE__*/ _jsx(DocItem, {
                                    node: att
                                }, att.file.attachmentId))
                        ]
                    })
                ]
            })
        ]
    });
}
function SectionHeader({ children, count }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex items-center gap-2 mb-2",
        children: [
            /*#__PURE__*/ _jsx("h4", {
                className: "text-[11px] font-semibold text-gray-400 uppercase tracking-widest",
                children: children
            }),
            count != null && /*#__PURE__*/ _jsx("span", {
                className: "text-[10px] text-gray-400 bg-gray-100 rounded-full px-1.5 py-0.5",
                children: count
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "flex-1 h-px bg-gray-100"
            })
        ]
    });
}
// ─── Main ─────────────────────────────────────────────────────────────────────
export const DocumentRelationshipGraph = /*#__PURE__*/ memo(function DocumentRelationshipGraph({ files }) {
    const { baseDoc, baseAttachments, sortedGroups, supportingDocs, qaDocs } = useMemo(()=>categorizeFiles(files), [
        files
    ]);
    const latestGroup = sortedGroups[0];
    const olderGroups = sortedGroups.slice(1);
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-6",
        children: [
            sortedGroups.length > 0 && /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx(SectionHeader, {
                        children: "Amendment Timeline"
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "ml-1",
                        children: [
                            latestGroup && /*#__PURE__*/ _jsx(TimelineNode, {
                                group: latestGroup,
                                isLatest: true,
                                isLast: olderGroups.length === 0 && !baseDoc,
                                defaultOpen: true
                            }),
                            olderGroups.map((g, i)=>/*#__PURE__*/ _jsx(TimelineNode, {
                                    group: g,
                                    isLatest: false,
                                    isLast: i === olderGroups.length - 1 && !baseDoc,
                                    defaultOpen: false
                                }, g.number))
                        ]
                    })
                ]
            }),
            (baseDoc || baseAttachments.length > 0) && /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx(SectionHeader, {
                        children: "Base Solicitation"
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "relative flex gap-4 ml-1",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "flex flex-col items-center flex-shrink-0 w-6",
                                children: /*#__PURE__*/ _jsx("div", {
                                    className: "w-3 h-3 rounded-full ring-4 ring-white bg-gray-400 z-10"
                                })
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex-1 min-w-0",
                                children: [
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "text-sm font-semibold text-gray-600 mb-1",
                                        children: "Original Package"
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "space-y-0",
                                        children: [
                                            baseDoc && /*#__PURE__*/ _jsx(DocItem, {
                                                node: baseDoc
                                            }),
                                            baseAttachments.map((att)=>/*#__PURE__*/ _jsx(DocItem, {
                                                    node: att
                                                }, att.file.attachmentId))
                                        ]
                                    })
                                ]
                            })
                        ]
                    })
                ]
            }),
            supportingDocs.length > 0 && /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx(SectionHeader, {
                        count: supportingDocs.length,
                        children: "Supporting Documents"
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "space-y-0",
                        children: supportingDocs.map((doc)=>/*#__PURE__*/ _jsx(DocItem, {
                                node: doc
                            }, doc.file.attachmentId))
                    })
                ]
            }),
            qaDocs.length > 0 && /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx(SectionHeader, {
                        count: qaDocs.length,
                        children: "Q&A Documents"
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "space-y-0",
                        children: qaDocs.map((doc)=>/*#__PURE__*/ _jsx(DocItem, {
                                node: doc
                            }, doc.file.attachmentId))
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "flex flex-wrap gap-4 pt-4 border-t border-gray-100",
                children: [
                    {
                        label: 'Amendments',
                        value: sortedGroups.length,
                        color: 'bg-amber-500'
                    },
                    {
                        label: 'Attachments',
                        value: supportingDocs.length + baseAttachments.length,
                        color: 'bg-gray-400'
                    },
                    {
                        label: 'Q&A',
                        value: qaDocs.length,
                        color: 'bg-cyan-500'
                    },
                    {
                        label: 'Total Files',
                        value: files.length,
                        color: 'bg-blue-500'
                    }
                ].map((stat)=>/*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-2",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: `w-2 h-2 rounded-full ${stat.color}`
                            }),
                            /*#__PURE__*/ _jsxs("span", {
                                className: "text-xs text-gray-500",
                                children: [
                                    stat.value,
                                    " ",
                                    stat.label
                                ]
                            })
                        ]
                    }, stat.label))
            })
        ]
    });
});
